import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { venueBookingsAPI } from '../../services/api';
import PitchSectionSelector from './PitchSectionSelector';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  venueId: number;
  venueSections: number;
  /** A részek nevei (név híján "N. rész") */
  sectionNames?: string[];
  prefilledStartTime?: string;
  prefilledSection?: number | null;
}

const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  venueId,
  venueSections,
  sectionNames,
  prefilledStartTime,
  prefilledSection,
}) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [purpose, setPurpose] = useState('');
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setPurpose('');
      setSaving(false);

      if (prefilledStartTime) {
        setStartTime(prefilledStartTime);
        // Default end time: start + 1 hour
        const start = new Date(prefilledStartTime);
        start.setHours(start.getHours() + 1);
        setEndTime(formatDateTimeLocal(start));
      } else {
        setStartTime('');
        setEndTime('');
      }

      // Rácsból egy adott rész oszlopára kattintva csak az a rész indul kijelölve,
      // egyébként a teljes pálya
      setSelectedSections(
        prefilledSection != null && prefilledSection >= 1 && prefilledSection <= venueSections
          ? [prefilledSection]
          : Array.from({ length: venueSections }, (_, i) => i + 1),
      );
    }
  }, [isOpen, prefilledStartTime, prefilledSection, venueSections]);

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value) {
      const start = new Date(value);
      start.setHours(start.getHours() + 1);
      setEndTime(formatDateTimeLocal(start));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      await venueBookingsAPI.createBooking({
        venueId,
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        purpose: purpose || undefined,
        sections:
          selectedSections.length > 0 && selectedSections.length < venueSections
            ? selectedSections.join(',')
            : undefined,
      });
      onSave();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.response?.data ||
        'Hiba történt a foglalás mentése során.';
      setError(typeof message === 'string' ? message : 'Hiba történt a foglalás mentése során.');
    } finally {
      setSaving(false);
    }
  };

  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label={'Pályafoglalás'}>
      <div className="flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="modal-panel-mobile relative flex w-full flex-col transform bg-white shadow-xl transition-all md:max-w-md md:rounded-lg md:overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 md:px-6 md:py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Új foglalás
            </h3>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto bg-white px-4 pt-4 pb-safe-4 md:px-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                    Kezdés *
                  </label>
                  <input
                    type="datetime-local"
                    id="startTime"
                    required
                    value={startTime}
                    onChange={(e) => handleStartTimeChange(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                    Befejezés *
                  </label>
                  <input
                    type="datetime-local"
                    id="endTime"
                    required
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="purpose" className="block text-sm font-medium text-gray-700">
                    Cél / megjegyzés
                  </label>
                  <input
                    type="text"
                    id="purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                    placeholder="Pl. Edzes, Megbeszeles..."
                  />
                </div>

                {venueSections > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Pályarész
                    </label>
                    <PitchSectionSelector
                      sectionCount={venueSections}
                      selected={selectedSections}
                      onChange={setSelectedSections}
                      sectionNames={sectionNames}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0 bg-gray-50 pb-safe">
              <div className="px-4 py-3 flex flex-col gap-2 md:flex-row-reverse md:gap-3 md:px-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full md:w-auto inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Mentés...' : 'Foglalás mentése'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full md:w-auto inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Mégse
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
